import type { QueryClient } from '@tanstack/react-query';
import {
  GetOneLinkFeatureConfigDocument,
  serverSideGetOneLinkFeatureConfigQuery,
} from '../generated/queries';
import type { Conditions } from '@dx-shared/rule-engine';
import { RuleEngine } from '@dx-shared/rule-engine';
import type { SupportedLanguage } from './types';

type AppName = string;
type AkamaiName = string;
type Ctyhocn = string;

export type OneLinkConfig =
  | {
      appPaths: Record<AppName, AkamaiName>;
      languages?: Partial<Record<SupportedLanguage, AppName[]>>;
      whitelist?: Record<AppName, Record<SupportedLanguage, Ctyhocn>>;
      conditions: Conditions;
      supportedLanguages: SupportedLanguage[];
    }
  | undefined;
export type OneLinkPageProps = Partial<
  Pick<ReturnType<typeof getOneLinkProps>, 'appName' | 'oneLinkConfig' | 'isOneLink'>
>;

/**
 * Determines if the locale, app, and url is supported in the OneLink configuration (`config-ui-translate-onelink`).
 * @param locale - The language code to check.
 * @param appName - The name of the application.
 * @param url - The URL to check @note may not be in the rule conditions.
 * @param oneLinkConfig - The OneLink configuration.
 * @returns True if the locale, app, and url is supported in the OneLink configuration, false otherwise.
 */
export function isInOneLinkConfig(
  locale: string,
  appName: string,
  url: string,
  oneLinkConfig?: OneLinkConfig | null
) {
  try {
    if (oneLinkConfig && locale) {
      const configLocale = locale.match(/^(en-gb|zh-hans|zh-hant|[a-z]+)/)?.[0] || 'en';
      const conditions = oneLinkConfig.conditions;
      const urlObj = /^https?/.test(url) ? new URL(url) : new URL(url, 'https://www.hilton.com');
      const pathName = urlObj.pathname;
      const ruleEngineResult = conditions
        ? new RuleEngine({
            name: 'onelink-conditions',
            conditions,
          }).run({ language: configLocale, url: pathName, appName })
        : false;
      return ruleEngineResult;
    }
    return false;
  } catch {
    return false;
  }
}

export function getOneLinkProps(
  ...[locale, appName, url, oneLinkConfig]: Parameters<typeof isInOneLinkConfig>
) {
  const isOneLink = isInOneLinkConfig(locale, appName, url, oneLinkConfig);
  const sstLocale = isOneLink ? 'en' : locale;
  return {
    appName,
    oneLinkConfig,
    sstLocale,
    isOneLink,
  };
}

export const ONELINK_CONFIG_NAME = 'config-ui-translate-onelink';
export const ONELINK_CONFIG_ORIGINAL_OP_NAME = GetOneLinkFeatureConfigDocument.originalOpName;

export async function serverSideOneLinkConfig(queryClient: QueryClient) {
  return serverSideGetOneLinkFeatureConfigQuery(queryClient)?.then((data) => {
    return (
      (data.featureConfigs.find((config) => config.name === ONELINK_CONFIG_NAME)
        ?.config as OneLinkConfig) || null
    );
  });
}

export const replaceLang = (stringValue: string, lang: string) =>
  stringValue.replace('__LANG__', lang);
